import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { RefusalNotice, useWritesRefused } from '../components/SuspendedNotice.js';
import { Alert, Button, Card, SelectField } from '../components/ui.js';
import { ApiError } from '../lib/api.js';
import { formatCredits, formatDate } from '../lib/format.js';
import { extendSites, fetchOrderTypes, fetchSites, type Site } from '../lib/ordersApi.js';
import { useSession } from '../session/SessionProvider.js';

/**
 * "Your sites" — ruling C-1.
 *
 * Renewal used to start from the order form's client picker: choose a client,
 * choose an order type, choose a site. Three decisions to answer the only
 * question anyone actually has, which is "which of my sites is running out?".
 *
 * So it starts from the site. Tick one or several, choose a term, done. The
 * batch is placed in a single request and the server refuses it as a whole, so
 * ticking five with credits for three gives you a clear refusal rather than an
 * arbitrary three orders.
 */

/** How urgent this site is, said in words rather than a colour alone. */
function describeRemaining(liveUntil: string | null): { text: string; tone: string } {
  if (!liveUntil) return { text: 'No end date', tone: 'bg-sunken text-fg-2' };

  const days = Math.round(
    (Date.parse(`${liveUntil}T00:00:00Z`) - Date.parse(`${new Date().toISOString().slice(0, 10)}T00:00:00Z`)) /
      86_400_000,
  );

  if (days < 0) {
    return {
      text: `Ended ${formatDate(liveUntil)}`,
      tone: 'bg-danger-soft text-danger',
    };
  }
  if (days <= 45) {
    return {
      text: `Ends ${formatDate(liveUntil)} · ${days} ${days === 1 ? 'day' : 'days'} left`,
      tone: 'bg-warn-soft text-warn',
    };
  }
  return { text: `Paid until ${formatDate(liveUntil)}`, tone: 'bg-sunken text-fg-2' };
}

export function SitesPage(): React.JSX.Element {
  const navigate = useNavigate();
  const { session, refresh } = useSession();
  // Suspended, or an operator viewing read-only - WAIT-B defect 3.
  const { refused: suspended } = useWritesRefused();

  const [sites, setSites] = useState<Site[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [years, setYears] = useState(1);
  const [maxYears, setMaxYears] = useState(5);
  const [perYear, setPerYear] = useState(50);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [sitesResponse, typesResponse] = await Promise.all([fetchSites(), fetchOrderTypes()]);
      setSites(sitesResponse.sites);
      const extension = typesResponse.orderTypes.find((type) => type.type === 'year_extension');
      if (extension) {
        setMaxYears(extension.maxYears);
        setPerYear(extension.creditsPerYear);
      }
      setError(null);
    } catch {
      setError('We could not load your sites.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  function toggle(siteId: string): void {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(siteId)) next.delete(siteId);
      else next.add(siteId);
      return next;
    });
  }

  const available = session?.credits?.availableCredits ?? 0;
  const total = perYear * years * selected.size;
  const affordable = selected.size > 0 && available >= total;

  async function handleRenew(): Promise<void> {
    setError(null);
    setBusy(true);
    try {
      const response = await extendSites({ siteIds: [...selected], years });
      await refresh();
      const first = response.orders[0];
      // One site renewed goes straight to that order; several go to the list,
      // because there is no single order to land on.
      void navigate(response.orders.length === 1 && first ? `/orders/${first.id}` : '/orders', {
        replace: true,
      });
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.message : 'We could not place those renewals.',
      );
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <p className="text-sm text-fg-muted" role="status">
        Loading…
      </p>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Your sites</h1>
        <p className="mt-1 text-sm text-fg-muted">
          Every site we have built for you, and how long each one is paid for.
        </p>
      </div>

      {error ? <Alert>{error}</Alert> : null}

      {sites.length === 0 ? (
        <Card>
          <p className="text-sm font-medium text-fg">No sites yet</p>
          <p className="mt-1 text-sm text-fg-muted">
            Your sites appear here once a build goes live.
          </p>
          {suspended ? null : (
          <Link to="/orders/new" className="mt-4 inline-block text-sm font-medium text-fg underline">
            Order a site build
          </Link>
          )}
        </Card>
      ) : (
        <>
          <Card className="p-0">
            <ul className="divide-y divide-rule-soft">
              {sites.map((site) => {
                const remaining = describeRemaining(site.liveUntil);
                return (
                  <li key={site.id} className="flex items-center gap-4 px-6 py-4">
                    {/*
                      WAIT-B polish 14. The renewal controls went at P-4 and
                      the tick boxes stayed, so a suspended agency could tick
                      five sites and find nothing to do with them. A control
                      that leads nowhere is worse than no control.
                    */}
                    {suspended ? null : (
                      <input
                        type="checkbox"
                        id={`site-${site.id}`}
                        checked={selected.has(site.id)}
                        onChange={() => toggle(site.id)}
                        className="h-4 w-4 shrink-0 rounded border-rule-strong text-fg focus:ring-blue-ink"
                      />
                    )}
                    <label htmlFor={`site-${site.id}`} className="min-w-0 flex-1 cursor-pointer">
                      <span className="block text-sm font-medium text-fg">
                        {site.businessName}
                      </span>
                      <span className="block truncate text-sm text-fg-muted">
                        {site.liveUrl ?? site.customDomain ?? 'No URL yet'}
                      </span>
                      {/*
                        D-14 Item 9.3. The same handover the order page shows,
                        summarised: this is a list of sites, so the count
                        belongs here and the URLs themselves belong on the
                        order. Absent for sites delivered before migration
                        0018, which recorded none.
                      */}
                      {site.linkUrls.length > 0 ? (
                        <span className="block text-xs text-fg-muted">
                          {site.linkUrls.length}{' '}
                          {site.linkUrls.length === 1 ? 'link page' : 'link pages'}
                        </span>
                      ) : null}
                    </label>
                    <span
                      className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-medium ${remaining.tone}`}
                    >
                      {remaining.text}
                    </span>
                  </li>
                );
              })}
            </ul>
          </Card>

          <Card className="space-y-4">
            <div>
              <h2 className="text-sm font-semibold text-fg-muted">Keep them live</h2>
              <p className="mt-1 text-sm text-fg-muted">
                Tick the sites above, choose how long, and we will do the rest.
              </p>
            </div>

            {/*
              P-4. The list of sites above stays, because a suspended agency
              keeps read access on purpose (Item 2) and "when does my site run
              out" is exactly the thing they will want to know while this is
              being sorted out. It is only the renewal that goes.
            */}
            {suspended ? <RefusalNotice action="renew a site" /> : (
            <>
            <SelectField
              label="How many more years?"
              id="years"
              name="years"
              value={String(years)}
              onChange={(event) => setYears(Number(event.target.value))}
            >
              {Array.from({ length: maxYears }, (_, index) => index + 1).map((option) => (
                <option key={option} value={option}>
                  {option} {option === 1 ? 'year' : 'years'} · {perYear * option} credits per site
                </option>
              ))}
            </SelectField>

            <p className="text-sm text-fg">
              {selected.size === 0
                ? 'No sites chosen yet.'
                : `${selected.size} ${selected.size === 1 ? 'site' : 'sites'} × ${years} ${
                    years === 1 ? 'year' : 'years'
                  } · ${formatCredits(total)} set aside.`}
            </p>

            {selected.size > 0 && !affordable ? (
              <Alert>
                You need {formatCredits(total)} for this and have {formatCredits(available)}{' '}
                available.{' '}
                <Link to="/credits" className="underline">
                  Buy more credits
                </Link>
                .
              </Alert>
            ) : null}

            <Button
              busy={busy}
              disabled={!affordable}
              onClick={() => void handleRenew()}
            >
              {selected.size > 1 ? `Keep ${selected.size} sites live` : 'Keep this site live'}
            </Button>
            </>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
